import { useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { Dag } from '@dbt-labs/dbt-dag';

import { ExplorerDag, ExplorerDagNode } from '@/components/ExplorerDag';
import { LineageFloatingNavBar } from '@/components/Lineage/LineageFloatingNavBar';
import { useDbtCloudAccount } from '@/context/DbtCloudProvider';
import { useMeshProjects } from '@/hooks/discovery/meshProjects';
import { useLinkToExplorer } from '@/hooks/links';
import { useTargetEnvironmentType } from '@/hooks/useTargetEnvironmentType';
import { parseUniqueId } from '@/typings/parseUniqueId';
import { LineagePanelView } from '../../../components/Lineage/LineagePanelView';
import { ProjectPanelContents } from '../../../components/ProjectPanel/ProjectPanelContents';

const getPublicModelText = (count: number) => {
  if (count === 1) return '1 Public Model';
  return `${count} Public Models`;
};

export const MultiProjectLineageView = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { uniqueId, projectId, environmentId } = useMemo(() => {
    const uniqueId = searchParams.get('uniqueId');
    const projectId = searchParams.get('projectId');
    const environmentId = searchParams.get('environmentId');

    return {
      uniqueId: uniqueId ? parseUniqueId(uniqueId) : null,
      projectId: projectId ? parseInt(projectId) : null,
      environmentId: environmentId ? parseInt(environmentId) : null,
    };
  }, [searchParams]);

  const { accountId, accountName } = useDbtCloudAccount();
  const projectLineageLink = useLinkToExplorer('projectLineage');
  const navigate = useNavigate();

  const [projectsQuery] = useMeshProjects({ accountId });
  const getTargetEnvironmentType = useTargetEnvironmentType();
  const projects = projectsQuery.data?.account.meshProjects;

  const errorMessages = useMemo(() => {
    if (projectsQuery.error) {
      return [projectsQuery.error.message];
    }
    if (projectsQuery.data?.account.meshProjects?.length === 0) {
      return ['No mesh projects were found on this account'];
    }
  }, [projectsQuery.error, projectsQuery.data]);

  const childToParentMap = useMemo(() => {
    if (!projects) return {};
    const map: Record<string, string[]> = {};
    projects.forEach((project) => {
      project.dependentProjects.forEach((dependentProject) => {
        map[dependentProject.dbtCoreProject] ||= [];
        map[dependentProject.dbtCoreProject].push(project.projectId.toString());
      });
    });
    return map;
  }, [projects]);

  const projectNodes = useMemo(
    () =>
      projects?.map((project): ExplorerDagNode => {
        return {
          id: project.projectId.toString(),
          resourceType: 'project',
          projectId: project.projectId,
          environmentId: project.defaultEnvironmentId,
          label: project.dbtCloudProject || project.dbtCoreProject,
          dbtCloudProject: project.dbtCloudProject || '',
          parents: childToParentMap[project.dbtCoreProject] || [],
          sublabel: getPublicModelText(project.projectSummary?.publicModels ?? 0),
        };
      }) ?? [],
    [childToParentMap, projects],
  );

  const goToProject = (targetNode: Omit<ExplorerDagNode, 'parents'>) => {
    if (!accountId) return;
    navigate(
      projectLineageLink({
        accountId,
        projectId: targetNode.projectId,
        environmentType: getTargetEnvironmentType(targetNode.environmentId),
      }),
    );
  };

  const showPanel = useMemo(() => {
    return !!(projectId || uniqueId);
  }, [projectId, uniqueId]);

  return (
    <div
      data-testid="multi-project-lineage-view"
      className="relative flex h-full overflow-hidden"
    >
      <span className="relative h-full w-full flex-1">
        <ExplorerDag
          grain="account"
          nodeType="lens"
          status={
            errorMessages?.length
              ? 'error'
              : projectNodes?.length
                ? 'success'
                : 'loading'
          }
          nodes={projectNodes}
          activeDbtCloudProject={null}
          errorMessages={errorMessages}
          showResetButton={false}
          getContextMenuOptions={({ environmentId, ...targetResource }) => {
            if (!environmentId) throw new Error('No environmentId');
            return [
              {
                label: 'View project details',
                onSelect: () => {
                  goToProject({ ...targetResource, environmentId });
                },
              },
            ];
          }}
          onNodeInteraction={(interaction) => {
            if (interaction.interactionType === 'single_click') {
              const isProject = interaction.targetNode.resourceType === 'project';

              setSearchParams((params) => {
                if (isProject) {
                  params.delete('uniqueId');
                  params.set('projectId', interaction.targetNode.projectId.toString());
                } else {
                  params.delete('projectId');
                  params.set('uniqueId', interaction.targetNode.id);
                }
                if (interaction.targetNode.environmentId) {
                  params.set(
                    'environmentId',
                    interaction.targetNode.environmentId.toString(),
                  );
                }
                return params;
              });
            } else if (interaction.interactionType === 'double_click') {
              goToProject(interaction.targetNode);
            } else if (interaction.interactionType === 'clear_selection') {
              setSearchParams((params) => {
                params.delete('uniqueId');
                params.delete('projectId');
                params.delete('environmentId');
                return params;
              });
            }
          }}
        >
          <Dag.ZoomControls />
        </ExplorerDag>
        <div className="absolute left-0 right-0 top-0">
          <LineageFloatingNavBar
            granularity="account"
            view="lineage"
            accountId={accountId!}
            accountName={accountName}
          />
        </div>
      </span>
      <span
        className="flex-0 text-left"
        data-testid={showPanel ? 'project-panel' : 'no-panel'}
      >
        <LineagePanelView
          isOpen={showPanel}
          propsToFreeze={{ projectId, environmentId }}
        >
          {({ projectId, environmentId }) =>
            projectId && (
              <ProjectPanelContents
                projectId={projectId}
                environmentId={environmentId}
              />
            )
          }
        </LineagePanelView>
      </span>
    </div>
  );
};
